import React, { useEffect, useState } from 'react';
import I18n from '@/modules/i18n';
import useFlags from '@/hooks/useFlags';
import Tooltip from '@/components/common/Tooltip';
import { NumberField } from '@/components/common/Forms';
import { RequiredAsterisk } from '@/components/common/Forms/Utils';
import { GradeByType } from '@/components/staffer/NotebookGradeByMenu/types';
import {
  composeValidators, integer, maxValue, nonNegative, required,
} from '@/components/common/Forms/Validators';
import sectionStyles from '../Section.module.scss';
import useAssignablesStore from './store/useAssignablesStore';
import cn from '@/utils/cn';

const MaxScoreField = ({ form, formChangeValue }: { form: any, formChangeValue: (val, field) => void }) => {
  const initialValues = useAssignablesStore(state => state.initialValues);
  const score = useAssignablesStore(state => state.score);
  const selectedType = useAssignablesStore(state => state.selectedType);

  const { gradeByAssignment, usesAssignmentsBasedGrading } = useFlags();
  const formValues = (form.getState().values || {});
  const gradingNotebookByValue = formValues.grading_notebook_by;
  const maxScoreValue = formValues.max_score;
  const isReadingSelected = selectedType === 'sectionNotebook';
  const useScoreInput = (usesAssignmentsBasedGrading || gradeByAssignment) && isReadingSelected;
  const useABG = usesAssignmentsBasedGrading || gradingNotebookByValue === GradeByType.Assignment;
  const isReadingAndGradeByAssignment = (usesAssignmentsBasedGrading || gradeByAssignment)
    && isReadingSelected
    && useABG;

  const [useCustomScore, setUseCustomScore] = useState(false);

  useEffect(() => {
    setUseCustomScore(false);
  }, [formValues.lesson_id]);

  useEffect(() => {
    if (useCustomScore || maxScoreValue === null) return;
    if (!(initialValues && selectedType)) return;
    if (isReadingSelected && gradingNotebookByValue === null) return;

    const nextScore = isReadingAndGradeByAssignment ? (initialValues.maxScore || score) : score;
    formChangeValue(nextScore, 'max_score');
  }, [gradingNotebookByValue, initialValues, isReadingSelected, score, selectedType]);

  const scoreLabel = () => {
    const labelText = isReadingAndGradeByAssignment ? I18n.t('points_possible') : I18n.t('score');
    if (!useScoreInput) return <span className={sectionStyles.label}>{labelText}</span>;

    const tooltipText = `The total score can only be updated for assignments set as
      "Grade by Assignment." Edit the assignment to change the setting.`;

    return (
      <span className={cn({ [sectionStyles.label]: !isReadingAndGradeByAssignment, 'tw-pr-4': isReadingAndGradeByAssignment })}>
        {labelText}
        <RequiredAsterisk />
        <span className="tw-pl-1">
          <Tooltip
            content={tooltipText}
            size="medium"
            theme="white"
          >
            <span>
              <i aria-hidden className="fa fa-question-circle" />
              <span className="sr-only">
                {tooltipText}
              </span>
            </span>
          </Tooltip>
        </span>
      </span>
    );
  };

  const scoreContent = () => {
    if (!useScoreInput) return score;

    return (
      <NumberField
        hideLabel
        disabled={!isReadingAndGradeByAssignment}
        name="max_score"
        validate={isReadingAndGradeByAssignment
          ? composeValidators(required, integer, nonNegative, maxValue(100))
          : () => {}
        }
        onChange={(e) => {
          formChangeValue(e.target.value, 'max_score');
          setUseCustomScore(true);
        }}
      />
    );
  };

  return (
    <div className={sectionStyles.row}>
      <div className={`mb10 ${sectionStyles.fieldContainer}`}>
        {scoreLabel()}
        <span className={cn({ [sectionStyles.scoreField]: !isReadingAndGradeByAssignment })}>
          {scoreContent()}
        </span>
      </div>
    </div>
  );
};

export default MaxScoreField;
